import { IProduct, IProductVariant, OfferSubType, Product, TypeMapper } from '@jumpstart/core';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { doc, DocumentReference, setDoc, collection } from 'firebase/firestore';
import React, { useState } from 'react';
import JSONInput from 'react-json-editor-ajrm';
import locale from 'react-json-editor-ajrm/locale/en';
import { db } from '../../../core/firebase';
import { IMAGE_PLACEHOLDER } from '../../../core/utils';
import SearchProducts from '../../../widgets/SearchProducts';

export interface IProductSelection {
  product: IProduct;
  seller: string;
  variant: IProductVariant;
}

export interface IProductSelectionView {
  onAdd(products: IProductSelection[]): void;
}

export default function ProductSelectionView(props: IProductSelectionView) {
  const { onAdd } = props;
  const [products, setProducts] = useState<IProductSelection[]>([]);
  const [newProduct, setNewProduct] = useState<null | IProduct>(null);
  const [stagedProduct, setStagedProduct] = useState<null | IProduct>(null);
  const [stagedSeller, setStagedSeller] = useState<null | string>(null);
  const [adding, setAdding] = useState(false);

  const handleConfirm = () => {
    onAdd(products);
    setProducts([]);
    setAdding(false);
    setStagedSeller(null)
    setStagedProduct(null)
  }

  const handleAddNewProduct = () => {
    if (newProduct) {
      setDoc(doc(db, "scout9-products", newProduct.id), newProduct)
        .then(() => {
          setStagedProduct(newProduct);
        })
        .catch((err: Error) => {
          console.error('Failed to add product', err);
        });
    } else {
      console.error('No new product');
    }

  };

  return (
    <Grid container direction="column">
      <Dialog open={!!newProduct} fullScreen>
        <DialogActions
          style={{ width: '100%', position: 'absolute' }}
        >
          <IconButton onClick={() => setNewProduct(null)}><CloseIcon/></IconButton>
        </DialogActions>
        <DialogTitle>New Product</DialogTitle>
        <DialogContent>
          <JSONInput
            id='product'
            placeholder={newProduct}
            onChange={(event: IProduct) => {
              try {
                setNewProduct({
                  ...event,
                  types: TypeMapper.parentRelations(event.type, 'product')
                });
              } catch (e: any) {
                console.error(e.message);
                setNewProduct(event);
              }
            }}
            locale={locale}
            height='100%'
            width="100%"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewProduct(null)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddNewProduct} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Collapse in={!adding}>
        <List dense>
          {products.map((product: IProductSelection, index: number) =>
            <ListItem key={product.product.id} button>
              <ListItemAvatar><Avatar
                src={product.product.media ? product.product.media : IMAGE_PLACEHOLDER}/></ListItemAvatar>
              <ListItemText
                primary={product.product.name}
                secondary={`$${product.variant.pricing.offerPrice ? product.variant.pricing.offerPrice : 'N/A'}`}
              />
              <ListItemSecondaryAction>
                <IconButton onClick={() => {
                  const newProducts = [...products];
                  newProducts.splice(index, 1);
                  setProducts(newProducts);
                }}><DeleteIcon/></IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          )}
          {
            products.length > 0 ?
              <ListItem button onClick={handleConfirm}>
                <ListItemText>Confirm ({products.length} items)</ListItemText>
              </ListItem>
              : null
          }

        </List>

        <Button color="secondary" onClick={() => {
          setAdding(true);
        }}>Add Products</Button>
      </Collapse>
      <Collapse in={!!adding}>
        <Button onClick={() => {
          setNewProduct({
            ...Product.defaultProps(OfferSubType.UNKNOWN, 'UNKNOWN', 'unknown'),
            sellerMeta: {
              chewy: {
                ...Product.defaultRetailMetadata(
                  Product.defaultProps(OfferSubType.UNKNOWN, 'unknown', 'unknown'),
                  'chewy'
                ),
                variants: [
                  Product.defaultVariant(
                    Product.defaultProps(OfferSubType.UNKNOWN, 'unknown', 'unknown'),
                    'chewy',
                    'variant_1'
                  )
                ]
              }
            }
          });
        }
        }
        >Add Custom Product</Button>
        <Collapse in={!stagedProduct}>
          <SearchProducts onSelect={setStagedProduct}/>
        </Collapse>
        <Collapse in={!!stagedProduct && !stagedSeller}>
          <Typography variant="body2">Select Seller</Typography>
          <List dense>
            {
              Object.keys(stagedProduct ? stagedProduct.sellerMeta : {}).map((seller: string) =>
                <ListItem button key={seller} onClick={() => {
                  if (stagedSeller && stagedSeller === seller) {
                    setStagedSeller(null);
                  } else {
                    setStagedSeller(seller);
                  }
                }}>
                  <ListItemText>{seller}</ListItemText>
                </ListItem>
              )
            }
          </List>
          <Button onClick={() => {
            setStagedProduct(null);
          }}>Reset</Button>
        </Collapse>
        <Collapse in={!!stagedSeller && !!stagedProduct}>
          <Typography variant="body2">Select Variant</Typography>
          {
            stagedProduct && stagedSeller ? (

              <List dense>
                {
                  stagedProduct.sellerMeta[stagedSeller].variants.map((variant: IProductVariant) => {
                    console.log(variant);
                    let dimensions = '';
                    if (variant.size.width.value &&  variant.size.length.value && variant.size.length.measurement) {
                      // @TODO check units across
                      dimensions += variant.size.length.value
                      dimensions += 'x'
                      dimensions += variant.size.width.value
                      if (variant.size.height.value) {
                        dimensions += 'x'
                        dimensions += variant.size.height.value
                      }
                      dimensions += ` ${variant.size.length.measurement}`;
                    }
                    if (variant.size.weight && variant.size.measurement) {
                      dimensions += `, ${variant.size.weight} ${variant.size.measurement}`;
                    }
                    return (<ListItem button key={variant.id} onClick={() => {
                      if (stagedSeller && stagedProduct) {
                        const newProducts = [
                          ...products,
                          { product: stagedProduct, seller: stagedSeller, variant: variant }
                        ];
                        setProducts(newProducts);
                        setAdding(false);
                        setStagedProduct(null);
                        setStagedSeller(null);
                      } else {
                        console.error('No staged seller or product');
                      }
                    }}>
                      <ListItemText
                        primary={variant.name ? variant.name : (stagedProduct ? stagedProduct.name : '')}
                        secondary={<React.Fragment><Typography variant="body2">${variant.pricing.offerPrice ? variant.pricing.offerPrice : 'N/A'}</Typography><Typography variant="caption">{dimensions}</Typography></React.Fragment>}
                      />
                    </ListItem>)}
                  )
                }
              </List>

            ) : null
          }

          <Button onClick={() => {
            setStagedSeller(null);
          }}>Reset</Button>
        </Collapse>
        <Button onClick={() => {
          setAdding(false);
        }}>Cancel</Button>
      </Collapse>


    </Grid>
  );
}
